import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Chart interaction and animation utilities
 */

// Animation configurations
export const CHART_ANIMATIONS = {
  // Smooth entry animations
  entry: {
    duration: 800,
    easing: 'easeOutQuart',
    delay: (context) => context.dataIndex * 50
  },
  
  // Hover animations
  hover: {
    duration: 200,
    easing: 'easeOutCubic'
  },
  
  // Update animations
  update: {
    duration: 600,
    easing: 'easeInOutQuart'
  },
  
  // Loading animations
  loading: {
    duration: 1000,
    easing: 'easeInOutSine'
  },

  // Drill-down animations
  drillDown: {
    duration: 700,
    easing: 'easeInOutBack'
  },

  // Data transition animations
  dataTransition: {
    duration: 500,
    easing: 'easeInOutCubic'
  },

  // Zoom animations
  zoom: {
    duration: 400,
    easing: 'easeOutExpo'
  },

  // Highlight animations
  highlight: {
    duration: 300,
    easing: 'easeOutQuint'
  }
};

// Chart interaction handlers
export class ChartInteractionHandler {
  constructor(chartRef, options = {}) {
    this.chartRef = chartRef;
    this.options = {
      enableDrillDown: true,
      enableExport: true,
      enableTooltipDetails: true,
      enableDetailedViews: true,
      enableContextMenu: true,
      enableKeyboardNavigation: true,
      ...options
    };
    this.drillDownStack = [];
    this.originalData = null;
    this.detailViewCallbacks = new Map();
    this.contextMenuCallbacks = new Map();
    this.keyboardHandlers = new Map();
  }

  // Handle chart click events for drill-down functionality
  handleChartClick = (event, elements, chartData, onDrillDown) => {
    if (!this.options.enableDrillDown || !elements.length) return;

    const elementIndex = elements[0].index;
    const datasetIndex = elements[0].datasetIndex;
    
    // Store original data if this is the first drill-down
    if (!this.originalData) {
      this.originalData = { ...chartData };
    }

    // Call the drill-down handler if provided
    if (onDrillDown) {
      const drillDownData = onDrillDown(elementIndex, datasetIndex, chartData);
      if (drillDownData) {
        this.drillDownStack.push({
          data: chartData,
          elementIndex,
          datasetIndex,
          timestamp: Date.now()
        });
        return drillDownData;
      }
    }

    return null;
  };

  // Navigate back in drill-down hierarchy
  navigateBack = () => {
    if (this.drillDownStack.length === 0) return null;
    
    const previousLevel = this.drillDownStack.pop();
    return previousLevel.data;
  };

  // Reset to original data
  resetToRoot = () => {
    this.drillDownStack = [];
    const rootData = this.originalData;
    this.originalData = null;
    return rootData;
  };

  // Get current drill-down level
  getCurrentLevel = () => {
    return this.drillDownStack.length;
  };

  // Get drill-down path for breadcrumb navigation
  getDrillDownPath = () => {
    return this.drillDownStack.map((level, index) => ({
      level: index + 1,
      elementIndex: level.elementIndex,
      datasetIndex: level.datasetIndex,
      timestamp: level.timestamp
    }));
  };

  // Register detailed view callback for specific chart elements
  registerDetailView = (elementType, callback) => {
    this.detailViewCallbacks.set(elementType, callback);
  };

  // Handle detailed view requests
  showDetailedView = (elementIndex, datasetIndex, chartData) => {
    if (!this.options.enableDetailedViews) return null;

    const elementType = this.getElementType(elementIndex, datasetIndex, chartData);
    const callback = this.detailViewCallbacks.get(elementType);
    
    if (callback) {
      return callback({
        elementIndex,
        datasetIndex,
        chartData,
        element: this.getElementData(elementIndex, datasetIndex, chartData)
      });
    }

    return null;
  };

  // Get element type for detailed view routing
  getElementType = (elementIndex, datasetIndex, chartData) => {
    // Determine element type based on chart structure
    if (chartData.datasets && chartData.datasets[datasetIndex]) {
      const dataset = chartData.datasets[datasetIndex];
      return dataset.type || 'default';
    }
    return 'default';
  };

  // Get element data for detailed views
  getElementData = (elementIndex, datasetIndex, chartData) => {
    if (!chartData.labels || !chartData.datasets) return null;

    return {
      label: chartData.labels[elementIndex],
      value: chartData.datasets[datasetIndex]?.data[elementIndex],
      dataset: chartData.datasets[datasetIndex],
      index: elementIndex,
      datasetIndex
    };
  };

  // Handle context menu interactions
  handleContextMenu = (event, elements, chartData) => {
    if (!this.options.enableContextMenu || !elements.length) return false;

    event.preventDefault();
    
    const elementIndex = elements[0].index;
    const datasetIndex = elements[0].datasetIndex;
    const elementType = this.getElementType(elementIndex, datasetIndex, chartData);
    const callback = this.contextMenuCallbacks.get(elementType);

    if (callback) {
      callback({
        event,
        elementIndex,
        datasetIndex,
        chartData,
        element: this.getElementData(elementIndex, datasetIndex, chartData),
        position: { x: event.clientX, y: event.clientY }
      });
      return true;
    }

    return false;
  };

  // Register context menu callback
  registerContextMenu = (elementType, callback) => {
    this.contextMenuCallbacks.set(elementType, callback);
  };

  // Handle keyboard navigation
  handleKeyboardNavigation = (event, chartData) => {
    if (!this.options.enableKeyboardNavigation) return false;

    const handler = this.keyboardHandlers.get(event.key);
    if (handler) {
      return handler(event, chartData);
    }

    return false;
  };

  // Register keyboard handler
  registerKeyboardHandler = (key, handler) => {
    this.keyboardHandlers.set(key, handler);
  };

  // Enhanced click handler with multiple interaction types
  handleAdvancedClick = (event, elements, chartData, options = {}) => {
    if (!elements.length) return null;

    const elementIndex = elements[0].index;
    const datasetIndex = elements[0].datasetIndex;
    
    // Determine interaction type based on modifiers
    if (event.ctrlKey || event.metaKey) {
      // Ctrl/Cmd + Click for detailed view
      return this.showDetailedView(elementIndex, datasetIndex, chartData);
    } else if (event.shiftKey) {
      // Shift + Click for multi-select (if supported)
      return this.handleMultiSelect(elementIndex, datasetIndex, chartData);
    } else if (event.altKey) {
      // Alt + Click for alternative action
      return this.handleAlternativeAction(elementIndex, datasetIndex, chartData);
    } else {
      // Regular click for drill-down
      return this.handleChartClick(event, elements, chartData, options.onDrillDown);
    }
  };

  // Handle multi-select interactions
  handleMultiSelect = (elementIndex, datasetIndex, chartData) => {
    // Implementation for multi-select functionality
    return null;
  };

  // Handle alternative actions
  handleAlternativeAction = (elementIndex, datasetIndex, chartData) => {
    // Implementation for alternative actions
    return null;
  };
}

// Chart export utilities
export class ChartExporter {
  constructor(chartRef, chartTitle = 'Chart') {
    this.chartRef = chartRef;
    this.chartTitle = chartTitle;
  }

  // Export chart as PNG image
  async exportAsPNG(filename, options = {}) {
    try {
      const chart = this.chartRef.current;
      if (!chart) throw new Error('Chart reference not found');

      const canvas = chart.canvas;
      const link = document.createElement('a');
      
      // Configure export options
      const exportOptions = {
        backgroundColor: options.backgroundColor || '#ffffff',
        quality: options.quality || 1.0,
        ...options
      };

      // Create a temporary canvas with background color
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;

      // Fill background
      tempCtx.fillStyle = exportOptions.backgroundColor;
      tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
      
      // Draw the chart
      tempCtx.drawImage(canvas, 0, 0);

      // Download the image
      link.download = filename || `${this.chartTitle.replace(/\s+/g, '_')}_${Date.now()}.png`;
      link.href = tempCanvas.toDataURL('image/png', exportOptions.quality);
      link.click();

      return true;
    } catch (error) {
      console.error('Error exporting chart as PNG:', error);
      throw error;
    }
  }

  // Export chart as PDF
  async exportAsPDF(filename, options = {}) {
    try {
      const chart = this.chartRef.current;
      if (!chart) throw new Error('Chart reference not found');

      const canvas = chart.canvas;
      
      // Configure PDF options
      const pdfOptions = {
        orientation: options.orientation || 'landscape',
        unit: 'mm',
        format: options.format || 'a4',
        ...options
      };

      const pdf = new jsPDF(pdfOptions);
      
      // Calculate dimensions
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const canvasAspectRatio = canvas.width / canvas.height;
      
      let imgWidth = pdfWidth - 20; // 10mm margin on each side
      let imgHeight = imgWidth / canvasAspectRatio;
      
      // Adjust if height exceeds page
      if (imgHeight > pdfHeight - 40) {
        imgHeight = pdfHeight - 40;
        imgWidth = imgHeight * canvasAspectRatio;
      }

      // Add title
      pdf.setFontSize(16);
      pdf.text(this.chartTitle, pdfWidth / 2, 20, { align: 'center' });

      // Add chart image
      const imgData = canvas.toDataURL('image/png', 1.0);
      const xOffset = (pdfWidth - imgWidth) / 2;
      const yOffset = 30;
      
      pdf.addImage(imgData, 'PNG', xOffset, yOffset, imgWidth, imgHeight);

      // Add timestamp
      pdf.setFontSize(8);
      pdf.text(`Generated on: ${new Date().toLocaleString()}`, 10, pdfHeight - 10);

      // Save the PDF
      const pdfFilename = filename || `${this.chartTitle.replace(/\s+/g, '_')}_${Date.now()}.pdf`;
      pdf.save(pdfFilename);

      return true;
    } catch (error) {
      console.error('Error exporting chart as PDF:', error);
      throw error;
    }
  }

  // Export chart data as CSV
  exportAsCSV(chartData, filename, options = {}) {
    try {
      const csvOptions = {
        includeHeaders: true,
        delimiter: ',',
        ...options
      };

      let csvContent = '';
      
      // Add headers
      if (csvOptions.includeHeaders && chartData.labels) {
        const headers = ['Label', ...chartData.datasets.map(dataset => dataset.label)];
        csvContent += headers.join(csvOptions.delimiter) + '\n';
      }

      // Add data rows
      if (chartData.labels && chartData.datasets) {
        chartData.labels.forEach((label, index) => {
          const row = [
            label,
            ...chartData.datasets.map(dataset => dataset.data[index] || '')
          ];
          csvContent += row.join(csvOptions.delimiter) + '\n';
        });
      }

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const csvFilename = filename || `${this.chartTitle.replace(/\s+/g, '_')}_data_${Date.now()}.csv`;
      
      link.href = URL.createObjectURL(blob);
      link.download = csvFilename;
      link.click();

      return true;
    } catch (error) {
      console.error('Error exporting chart as CSV:', error);
      throw error;
    }
  }

  // Export chart container as high-quality image using html2canvas
  async exportContainerAsImage(containerElement, filename, options = {}) {
    try {
      const exportOptions = {
        backgroundColor: options.backgroundColor || '#ffffff',
        scale: options.scale || 2,
        useCORS: true,
        allowTaint: true,
        ...options
      };

      const canvas = await html2canvas(containerElement, exportOptions);
      
      // Download the image
      const link = document.createElement('a');
      link.download = filename || `chart_container_${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      return true;
    } catch (error) {
      console.error('Error exporting container as image:', error);
      throw error;
    }
  }

  // Export chart as SVG (vector format)
  async exportAsSVG(filename, options = {}) {
    try {
      const chart = this.chartRef.current;
      if (!chart) throw new Error('Chart reference not found');

      // Create SVG representation of the chart
      const canvas = chart.canvas;
      const ctx = canvas.getContext('2d');
      
      // Get chart dimensions
      const width = canvas.width;
      const height = canvas.height;
      
      // Create SVG string
      let svgString = `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">`;
      
      // Add background
      const bgColor = options.backgroundColor || '#ffffff';
      svgString += `<rect width="100%" height="100%" fill="${bgColor}"/>`;
      
      // Convert canvas to data URL and embed in SVG
      const dataURL = canvas.toDataURL('image/png');
      svgString += `<image href="${dataURL}" width="${width}" height="${height}"/>`;
      svgString += '</svg>';

      // Download SVG
      const blob = new Blob([svgString], { type: 'image/svg+xml' });
      const link = document.createElement('a');
      link.download = filename || `${this.chartTitle.replace(/\s+/g, '_')}_${Date.now()}.svg`;
      link.href = URL.createObjectURL(blob);
      link.click();

      return true;
    } catch (error) {
      console.error('Error exporting chart as SVG:', error);
      throw error;
    }
  }

  // Export chart data as JSON
  exportAsJSON(chartData, filename, options = {}) {
    try {
      const exportData = {
        title: this.chartTitle,
        timestamp: new Date().toISOString(),
        chartData: chartData,
        metadata: {
          exportedBy: 'Budget Dashboard',
          version: '1.0.0',
          ...options.metadata
        }
      };

      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const link = document.createElement('a');
      
      const jsonFilename = filename || `${this.chartTitle.replace(/\s+/g, '_')}_data_${Date.now()}.json`;
      link.download = jsonFilename;
      link.href = URL.createObjectURL(blob);
      link.click();

      return true;
    } catch (error) {
      console.error('Error exporting chart as JSON:', error);
      throw error;
    }
  }

  // Export chart as Excel workbook with multiple sheets
  async exportAsExcel(chartData, filename, options = {}) {
    try {
      // This would require a library like xlsx or exceljs
      // For now, we'll export as CSV with enhanced formatting
      const csvOptions = {
        includeHeaders: true,
        includeMetadata: true,
        delimiter: ',',
        ...options
      };

      let csvContent = '';
      
      // Add metadata section
      if (csvOptions.includeMetadata) {
        csvContent += `Chart Title,${this.chartTitle}\n`;
        csvContent += `Export Date,${new Date().toLocaleString()}\n`;
        csvContent += `Data Points,${chartData.labels?.length || 0}\n`;
        csvContent += `Datasets,${chartData.datasets?.length || 0}\n`;
        csvContent += '\n'; // Empty line separator
      }

      // Add headers
      if (csvOptions.includeHeaders && chartData.labels) {
        const headers = ['Label', ...chartData.datasets.map(dataset => dataset.label)];
        csvContent += headers.join(csvOptions.delimiter) + '\n';
      }

      // Add data rows with enhanced formatting
      if (chartData.labels && chartData.datasets) {
        chartData.labels.forEach((label, index) => {
          const row = [
            `"${label}"`, // Quote labels to handle commas
            ...chartData.datasets.map(dataset => {
              const value = dataset.data[index];
              return typeof value === 'number' ? value.toFixed(2) : value || '';
            })
          ];
          csvContent += row.join(csvOptions.delimiter) + '\n';
        });
      }

      // Add summary statistics
      if (csvOptions.includeMetadata) {
        csvContent += '\n'; // Empty line separator
        csvContent += 'Summary Statistics\n';
        
        chartData.datasets.forEach(dataset => {
          const values = dataset.data.filter(v => typeof v === 'number');
          if (values.length > 0) {
            const sum = values.reduce((a, b) => a + b, 0);
            const avg = sum / values.length;
            const min = Math.min(...values);
            const max = Math.max(...values);
            
            csvContent += `${dataset.label} - Total,${sum.toFixed(2)}\n`;
            csvContent += `${dataset.label} - Average,${avg.toFixed(2)}\n`;
            csvContent += `${dataset.label} - Minimum,${min.toFixed(2)}\n`;
            csvContent += `${dataset.label} - Maximum,${max.toFixed(2)}\n`;
          }
        });
      }

      // Download enhanced CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const csvFilename = filename || `${this.chartTitle.replace(/\s+/g, '_')}_enhanced_${Date.now()}.csv`;
      
      link.href = URL.createObjectURL(blob);
      link.download = csvFilename;
      link.click();

      return true;
    } catch (error) {
      console.error('Error exporting enhanced CSV:', error);
      throw error;
    }
  }

  // Batch export multiple formats
  async exportMultipleFormats(chartData, baseFilename, formats = ['png', 'pdf', 'csv']) {
    const results = [];
    
    for (const format of formats) {
      try {
        switch (format) {
          case 'png':
            await this.exportAsPNG(`${baseFilename}.png`);
            results.push({ format: 'PNG', success: true });
            break;
          case 'pdf':
            await this.exportAsPDF(`${baseFilename}.pdf`);
            results.push({ format: 'PDF', success: true });
            break;
          case 'csv':
            this.exportAsCSV(chartData, `${baseFilename}.csv`);
            results.push({ format: 'CSV', success: true });
            break;
          case 'svg':
            await this.exportAsSVG(`${baseFilename}.svg`);
            results.push({ format: 'SVG', success: true });
            break;
          case 'json':
            this.exportAsJSON(chartData, `${baseFilename}.json`);
            results.push({ format: 'JSON', success: true });
            break;
          default:
            results.push({ format: format.toUpperCase(), success: false, error: 'Unsupported format' });
        }
      } catch (error) {
        results.push({ format: format.toUpperCase(), success: false, error: error.message });
      }
    }

    return results;
  }
}

// Animation utilities
export const createChartAnimations = (animationType = 'entry') => {
  const config = CHART_ANIMATIONS[animationType] || CHART_ANIMATIONS.entry;
  
  return {
    animation: {
      duration: config.duration,
      easing: config.easing,
      delay: config.delay,
      onComplete: (animation) => {
        // Animation complete callback
        if (animation.chart) {
          animation.chart.canvas.style.opacity = '1';
        }
      },
      onProgress: (animation) => {
        // Animation progress callback
        const progress = animation.currentStep / animation.numSteps;
        if (animation.chart && animation.chart.canvas) {
          animation.chart.canvas.style.opacity = Math.min(0.3 + (progress * 0.7), 1);
        }
      }
    },
    animations: {
      tension: {
        duration: config.duration,
        easing: config.easing,
        from: 1,
        to: 0.3,
        loop: false
      },
      radius: {
        duration: config.duration,
        easing: config.easing,
        from: 0,
        to: (context) => {
          return context.parsed ? 4 : 0;
        }
      }
    }
  };
};

// Loading animation component
export const createLoadingAnimation = () => {
  return {
    animation: {
      duration: CHART_ANIMATIONS.loading.duration,
      easing: CHART_ANIMATIONS.loading.easing,
      loop: true,
      onProgress: (animation) => {
        const progress = animation.currentStep / animation.numSteps;
        const opacity = 0.3 + Math.sin(progress * Math.PI * 2) * 0.3;
        if (animation.chart && animation.chart.canvas) {
          animation.chart.canvas.style.opacity = opacity;
        }
      }
    }
  };
};

// Smooth transition utilities
export const createSmoothTransition = (fromData, toData, duration = 600) => {
  return {
    animation: {
      duration,
      easing: 'easeInOutQuart',
      onProgress: (animation) => {
        const progress = animation.currentStep / animation.numSteps;
        
        // Interpolate between old and new data
        if (animation.chart && animation.chart.data) {
          animation.chart.data.datasets.forEach((dataset, datasetIndex) => {
            if (fromData.datasets[datasetIndex] && toData.datasets[datasetIndex]) {
              dataset.data = dataset.data.map((value, index) => {
                const fromValue = fromData.datasets[datasetIndex].data[index] || 0;
                const toValue = toData.datasets[datasetIndex].data[index] || 0;
                return fromValue + (toValue - fromValue) * progress;
              });
            }
          });
        }
      }
    }
  };
};

// Advanced animation utilities
export const createDrillDownAnimation = (direction = 'in') => {
  const config = CHART_ANIMATIONS.drillDown;
  
  return {
    animation: {
      duration: config.duration,
      easing: config.easing,
      onProgress: (animation) => {
        const progress = animation.currentStep / animation.numSteps;
        const chart = animation.chart;
        
        if (chart && chart.canvas) {
          if (direction === 'in') {
            // Zoom in effect for drill-down
            const scale = 0.8 + (progress * 0.2);
            const opacity = 0.3 + (progress * 0.7);
            chart.canvas.style.transform = `scale(${scale})`;
            chart.canvas.style.opacity = opacity;
          } else {
            // Zoom out effect for drill-up
            const scale = 1.2 - (progress * 0.2);
            const opacity = 0.3 + (progress * 0.7);
            chart.canvas.style.transform = `scale(${scale})`;
            chart.canvas.style.opacity = opacity;
          }
        }
      },
      onComplete: (animation) => {
        if (animation.chart && animation.chart.canvas) {
          animation.chart.canvas.style.transform = 'scale(1)';
          animation.chart.canvas.style.opacity = '1';
        }
      }
    }
  };
};

// Highlight animation for specific elements
export const createHighlightAnimation = (elementIndices = []) => {
  const config = CHART_ANIMATIONS.highlight;
  
  return {
    animation: {
      duration: config.duration,
      easing: config.easing,
      onProgress: (animation) => {
        const progress = animation.currentStep / animation.numSteps;
        const chart = animation.chart;
        
        if (chart && chart.data && chart.data.datasets) {
          chart.data.datasets.forEach((dataset, datasetIndex) => {
            if (dataset.backgroundColor && Array.isArray(dataset.backgroundColor)) {
              dataset.backgroundColor = dataset.backgroundColor.map((color, index) => {
                if (elementIndices.includes(index)) {
                  // Highlight selected elements
                  const intensity = 0.5 + (Math.sin(progress * Math.PI * 4) * 0.3);
                  return color.replace(/[\d.]+\)$/g, `${intensity})`);
                }
                return color;
              });
            }
          });
        }
      }
    }
  };
};

// Staggered entry animation for multiple elements
export const createStaggeredAnimation = (staggerDelay = 100) => {
  return {
    animation: {
      duration: CHART_ANIMATIONS.entry.duration,
      easing: CHART_ANIMATIONS.entry.easing,
      delay: (context) => {
        // Stagger animation based on element position
        if (context.type === 'data' && context.dataIndex !== undefined) {
          return context.dataIndex * staggerDelay;
        }
        return 0;
      },
      onProgress: (animation) => {
        const progress = animation.currentStep / animation.numSteps;
        const chart = animation.chart;
        
        if (chart && chart.canvas) {
          // Fade in with slight scale effect
          const scale = 0.95 + (progress * 0.05);
          const opacity = progress;
          chart.canvas.style.transform = `scale(${scale})`;
          chart.canvas.style.opacity = opacity;
        }
      }
    }
  };
};

// Morphing animation for data changes
export const createMorphingAnimation = (morphType = 'smooth') => {
  return {
    animation: {
      duration: CHART_ANIMATIONS.dataTransition.duration,
      easing: CHART_ANIMATIONS.dataTransition.easing,
      onProgress: (animation) => {
        const progress = animation.currentStep / animation.numSteps;
        
        if (morphType === 'elastic') {
          // Elastic morphing effect
          const elasticProgress = progress < 0.5 
            ? 2 * progress * progress 
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;
          
          // Apply elastic transformation to chart elements
          if (animation.chart && animation.chart.canvas) {
            const scaleY = 0.8 + (elasticProgress * 0.2);
            animation.chart.canvas.style.transform = `scaleY(${scaleY})`;
          }
        }
      },
      onComplete: (animation) => {
        if (animation.chart && animation.chart.canvas) {
          animation.chart.canvas.style.transform = 'scale(1)';
        }
      }
    }
  };
};

// Hover effect utilities
export const createHoverEffects = (options = {}) => {
  const {
    enableGlow = true,
    enableScale = true,
    enableCrosshair = false,
    glowColor = 'rgba(59, 130, 246, 0.3)',
    scaleIntensity = 1.1
  } = options;

  return {
    onHover: (event, elements, chart) => {
      const canvas = chart.canvas;
      
      if (elements.length > 0) {
        canvas.style.cursor = 'pointer';
        
        // Add glow effect
        if (enableGlow) {
          canvas.style.filter = `drop-shadow(0 0 8px ${glowColor})`;
        }
        
        // Add scale effect to hovered element
        if (enableScale) {
          const element = elements[0];
          if (element.element) {
            element.element.options = {
              ...element.element.options,
              hoverBorderWidth: 4,
              hoverOffset: 12,
              hoverRadius: element.element.options.radius * scaleIntensity
            };
          }
        }

        // Add crosshair effect for line charts
        if (enableCrosshair && chart.config.type === 'line') {
          canvas.style.cursor = 'crosshair';
        }
      } else {
        canvas.style.cursor = 'default';
        canvas.style.filter = 'none';
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    },
    elements: {
      point: {
        hoverRadius: 10,
        hoverBorderWidth: 4,
        borderWidth: 2,
        radius: 5
      },
      bar: {
        hoverBorderWidth: 4,
        borderWidth: 2,
        hoverBorderRadius: 6
      },
      arc: {
        hoverOffset: 15,
        hoverBorderWidth: 4,
        borderWidth: 2
      }
    },
    // Enhanced hover animations
    animations: {
      hover: {
        duration: CHART_ANIMATIONS.hover.duration,
        easing: CHART_ANIMATIONS.hover.easing
      }
    }
  };
};

// Create advanced interaction effects
export const createInteractionEffects = (options = {}) => {
  const {
    enableRipple = true,
    enableParticles = false,
    enableTrails = false
  } = options;

  return {
    onClick: (event, elements, chart) => {
      if (!elements.length) return;

      const canvas = chart.canvas;
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      // Create ripple effect
      if (enableRipple) {
        createRippleEffect(canvas, x, y);
      }

      // Create particle effect
      if (enableParticles) {
        createParticleEffect(canvas, x, y);
      }

      // Create trail effect
      if (enableTrails) {
        createTrailEffect(canvas, x, y);
      }
    }
  };
};

// Ripple effect implementation
const createRippleEffect = (canvas, x, y) => {
  const ripple = document.createElement('div');
  ripple.style.position = 'absolute';
  ripple.style.left = `${x - 25}px`;
  ripple.style.top = `${y - 25}px`;
  ripple.style.width = '50px';
  ripple.style.height = '50px';
  ripple.style.borderRadius = '50%';
  ripple.style.background = 'rgba(59, 130, 246, 0.3)';
  ripple.style.transform = 'scale(0)';
  ripple.style.animation = 'ripple 0.6s ease-out';
  ripple.style.pointerEvents = 'none';
  ripple.style.zIndex = '1000';

  // Add ripple animation styles
  if (!document.getElementById('ripple-styles')) {
    const style = document.createElement('style');
    style.id = 'ripple-styles';
    style.textContent = `
      @keyframes ripple {
        to {
          transform: scale(4);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }

  canvas.parentElement.style.position = 'relative';
  canvas.parentElement.appendChild(ripple);

  setTimeout(() => {
    if (ripple.parentElement) {
      ripple.parentElement.removeChild(ripple);
    }
  }, 600);
};

// Particle effect implementation
const createParticleEffect = (canvas, x, y) => {
  const particleCount = 8;
  const particles = [];

  for (let i = 0; i < particleCount; i++) {
    const particle = document.createElement('div');
    particle.style.position = 'absolute';
    particle.style.left = `${x}px`;
    particle.style.top = `${y}px`;
    particle.style.width = '4px';
    particle.style.height = '4px';
    particle.style.borderRadius = '50%';
    particle.style.background = 'var(--primary)';
    particle.style.pointerEvents = 'none';
    particle.style.zIndex = '1000';

    const angle = (i / particleCount) * Math.PI * 2;
    const velocity = 50;
    const vx = Math.cos(angle) * velocity;
    const vy = Math.sin(angle) * velocity;

    particle.style.animation = `particle-${i} 0.8s ease-out forwards`;

    // Create unique animation for each particle
    const style = document.createElement('style');
    style.textContent = `
      @keyframes particle-${i} {
        to {
          transform: translate(${vx}px, ${vy}px);
          opacity: 0;
          scale: 0;
        }
      }
    `;
    document.head.appendChild(style);

    canvas.parentElement.appendChild(particle);
    particles.push(particle);

    setTimeout(() => {
      if (particle.parentElement) {
        particle.parentElement.removeChild(particle);
      }
      document.head.removeChild(style);
    }, 800);
  }
};

// Trail effect implementation
const createTrailEffect = (canvas, x, y) => {
  const trail = document.createElement('div');
  trail.style.position = 'absolute';
  trail.style.left = `${x - 2}px`;
  trail.style.top = `${y - 2}px`;
  trail.style.width = '4px';
  trail.style.height = '4px';
  trail.style.borderRadius = '50%';
  trail.style.background = 'var(--primary)';
  trail.style.boxShadow = '0 0 10px var(--primary)';
  trail.style.animation = 'trail 1s ease-out forwards';
  trail.style.pointerEvents = 'none';
  trail.style.zIndex = '1000';

  // Add trail animation styles
  if (!document.getElementById('trail-styles')) {
    const style = document.createElement('style');
    style.id = 'trail-styles';
    style.textContent = `
      @keyframes trail {
        0% {
          transform: scale(1);
          opacity: 1;
        }
        50% {
          transform: scale(3);
          opacity: 0.5;
        }
        100% {
          transform: scale(0);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }

  canvas.parentElement.appendChild(trail);

  setTimeout(() => {
    if (trail.parentElement) {
      trail.parentElement.removeChild(trail);
    }
  }, 1000);
};

// Tooltip enhancement utilities
export const createEnhancedTooltips = (customCallbacks = {}) => {
  return {
    tooltip: {
      backgroundColor: 'var(--panel)',
      titleColor: 'var(--text)',
      bodyColor: 'var(--text)',
      borderColor: 'var(--border)',
      borderWidth: 1,
      cornerRadius: 8,
      displayColors: true,
      animation: {
        duration: CHART_ANIMATIONS.hover.duration,
        easing: CHART_ANIMATIONS.hover.easing
      },
      callbacks: {
        title: (tooltipItems) => {
          if (customCallbacks.title) {
            return customCallbacks.title(tooltipItems);
          }
          return tooltipItems[0]?.label || '';
        },
        label: (context) => {
          if (customCallbacks.label) {
            return customCallbacks.label(context);
          }
          return `${context.dataset.label}: ${context.parsed.y?.toLocaleString() || context.parsed}`;
        },
        afterLabel: (context) => {
          if (customCallbacks.afterLabel) {
            return customCallbacks.afterLabel(context);
          }
          return '';
        },
        footer: (tooltipItems) => {
          if (customCallbacks.footer) {
            return customCallbacks.footer(tooltipItems);
          }
          return '';
        }
      }
    }
  };
};

export default {
  ChartInteractionHandler,
  ChartExporter,
  createChartAnimations,
  createLoadingAnimation,
  createSmoothTransition,
  createHoverEffects,
  createEnhancedTooltips,
  CHART_ANIMATIONS
};